import axios, { AxiosError } from "axios";
import axiosRetry from "axios-retry";
import { Dictionary } from "lodash";
import keycloak, { forceLogin, getAccessToken, logout, updateToken } from "./keycloak";
import { isAuthErrored } from "./utils/isAuthError";
import { promptConcurrentSessionChoice } from "./services/concurrentSessionBridge";
import config from "./config";

const setAuthorization = (token: string) =>
  (axios.defaults.headers.common["Authorization"] = `Bearer ${token}`);

const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

axios.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers["X-User-Timezone"] = getBrowserTimezone();
  return config;
});

// axios.defaults.baseURL = config.ruleServerUrl; // TODO: Need to switch everything api Server
const init = ({ token }: Dictionary<string>) => {
  axiosRetry(axios, {
    retries: 2,
    retryCondition: async (error: AxiosError) => {
      if (isAuthErrored(error as AxiosError<Dictionary<string>>)) {
        keycloak.onTokenExpired && keycloak.onTokenExpired();
        return true;
      }
      return false;
    },
  });
  setAuthorization(token);
};

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];
function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

function onRefreshed(newToken: string) {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
}
// Only 401 (invalid/expired token) should trigger a token refresh / re-login. A 403 is an
// authorization denial (requirePermission / module gate) that re-auth cannot fix — treating it
// like 401 caused an infinite refresh→forceLogin loop, so 403s now reject and surface normally.
const isUnauthorized = (error: AxiosError) => {
  return error.response?.status === 401;
};

// VAPT #15 (Concurrent User): the 409 the backend returns when this account is already active in
// another session — distinct from a plain 401, since this isn't "not authorized", it's "there's a
// choice to make" (see ClientError.conflict on the backend).
const isConcurrentSessionConflict = (error: AxiosError) => {
  const data = error.response?.data as Dictionary<any> | undefined;
  return error.response?.status === 409 && data?.details?.code === "CONCURRENT_SESSION";
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isConcurrentSessionConflict(error)) {
      const originalRequest = error.config;
      const choice = await promptConcurrentSessionChoice();

      if (choice === "cancel") {
        // Keep the other session — this one never proceeds. logout() clears our own tracking
        // pointer and ends only this browser's Keycloak session; the other one is untouched.
        await logout();
        return Promise.reject(error);
      }

      // "take-over": revoke the other session at Keycloak and claim the slot for this one, then
      // replay the request that triggered the conflict so the original action still completes.
      try {
        await axios.post(`${config.apiServerUrl}/users/session/take-over`, null, {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
        });
        return axios(originalRequest);
      } catch (takeOverError) {
        return Promise.reject(takeOverError);
      }
    }

    if (isUnauthorized(error)) {
      const originalRequest = error.config;
      if (!originalRequest._retry) {
        originalRequest._retry = true;

        if (!isRefreshing) {
          isRefreshing = true;
          try {
            const newToken = await updateToken();
            if (newToken) {
              onRefreshed(newToken);
              isRefreshing = false;
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return axios(originalRequest);
            }
          } catch (err) {
            isRefreshing = false;
            return Promise.reject(err);
          }
        }

        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(axios(originalRequest));
          });
        });
      } else {
        await forceLogin();
      }
    }
    throw error;
  }
);

const Axios = {
  init,
  setAuthorization,
};

export default Axios;
